import { useIntervalFn } from "@vueuse/core";
import { useFetch } from "~vue/api";
import type { WorkdayReviewStatusResponse } from "~vue/types/performance_management";
import { ref } from "vue";

const POLL_INTERVAL_MS = 5_000;

/**
 * Whether a performance chat is still waiting on its Workday review, read in the background (LAB-1789).
 *
 * `check()` asks once and keeps asking while the answer is yes. The server stops saying yes once the read
 * settles, or lapses with a lost task, so polling always ends. A failed request counts as not loading:
 * the indicator is a courtesy, and Nadia is told about the wait separately.
 */
export function useWorkdayReviewLoading(statusUrl: string | null) {
    const reviewLoading = ref(false);
    const { execute, data, isFetching } = useFetch(statusUrl || "", { immediate: false })
        .get()
        .json<WorkdayReviewStatusResponse>();
    // A hidden tab skips its turn rather than asking every few seconds for a chip nobody can see.
    const poll = useIntervalFn(
        () => {
            if (globalThis.document?.visibilityState !== "hidden") {
                void check();
            }
        },
        POLL_INTERVAL_MS,
        { immediate: false },
    );

    async function check() {
        if (!statusUrl || isFetching.value) {
            return;
        }
        await execute();
        reviewLoading.value = data.value?.review_loading === true;
        if (reviewLoading.value) {
            poll.resume();
        } else {
            poll.pause();
        }
    }

    return { reviewLoading, check };
}
